import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  APPLICATION_ENTITLEMENTS,
  CONTENT_APPLIED,
  CONTENT_COPY_UNKNOWN,
  ENTITLEMENTS_CHANGED_CONCURRENTLY,
  FIRST_ADMIN_NOT_INVITED,
  FIRST_ADMIN_SIGNED_IN,
  IDENTITY_TENANT_ID_MALFORMED,
  MAIL_SENT,
  applyDefaultContent,
  cancelFirstAdminInvite,
  entitlementLabelKey,
  getProvisioning,
  inviteFirstAdministrator,
  listContentLibrary,
  newestPerItem,
  resendFirstAdminActivation,
  setEntitlements,
  type ContentLibraryItem,
  type TenantProvisioning,
  type TenantProvisioningStep,
} from "./provisioningApi";
import {
  ACTIVATION_AWAITING_ACTIVATION,
  STEP_DONE,
  STEP_NOT_AVAILABLE,
  STEP_PENDING,
  activationBodyKey,
  activationKey,
  cancelOutcomeKey,
  compensationKey,
  stepBodyKey,
  stepStateKey,
  stepTitleKey,
} from "./provisioningSteps";
import { TenantFederationForm } from "./TenantFederationForm";
import { accessState, closesAccess, opensAccess, sameEntitlements } from "./tenantAccess";
import type { Tenant } from "./tenantsApi";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { formatDate } from "@web/lib/date";
import { Modal } from "@web/ui";

// The tenant's PROVISIONING section (ADR-0014 §7.1, §12 C-5) — four steps on the platform surface, each
// idempotent and separately retryable. It is the first section of the tenant's own page
// (`pages/TenantPage.tsx`); it was a dialog behind a row menu until that page existed.
//
// WHY IT IS A LIST OF STEPS AND NOT A SEQUENCE OF SCREENS. §7.1's requirement is that a step can fail at
// the customer's end "and must not strand the tenant created in step 1", which means the operator comes
// BACK to a half-provisioned tenant — possibly days later, possibly a different operator. A linear wizard
// that has to be walked from the beginning would make the common case (one step left) the slowest, and a
// "resume at step N" cursor would be the stored progress field this whole feature deliberately does not
// have. So every step is on screen with its own state, and each one's form is reachable on its own.
//
// THE STATE IS THE SERVER'S, ALWAYS. The status is read when the section mounts and re-read from each
// step's response, never patched locally: the backend derives every step from the fact the step produces,
// so a locally-advanced step could claim a success the data does not show. It is also what makes the
// interrupting path cost nothing — a reload mid-provisioning re-reads the same four facts.
//
// WHY THE READ IS HERE AND NOT ON THE LIST. `GET …/provisioning` writes a `TENANT_ACCESSED` audit row
// (`{"viewed":"tenant_provisioning"}`, FR-Q.35): reading a named tenant across the isolation boundary IS
// the audited event. Decorating the tenant TABLE with it would file one row per tenant nobody looked at,
// so the table's "provisioning finished" badge comes from the tenant LIST, which discloses no customer
// data and is deliberately unaudited. On THIS page the read happens on load, because opening a tenant's
// own page is the operator's explicit choice of that tenant — the audited read then records a crossing
// that really happened (product-owner decision, 6.10.2026).
//
// EVERY STEP IS SOMEBODY'S NOW, AND NOTHING HERE NAMES A FOLLOW-UP TASK. Step 2's federation half was
// C-6's and shipped with it; the `followUp` note this section used to render for it is gone, with the
// vocabulary behind it, exactly as step 4's went when D-2 built that step. What stands in its place is a
// real sub-section, `TenantFederationForm`, which says the one thing a C-6 console must say and a 200
// cannot: the arrangement is RECORDED and the realm does not carry it yet, because the API that answered
// holds no authority to write it (ADR-0014 §7.2).
//
// STEP 2 IS DONE BEFORE THE PERSON HAS ANSWERED, AND THAT IS WHY IT HAS THREE BUTTONS (ADR-0014 §7.5).
// Since 6.10.2026 an address the realm has never heard of is not a refusal: the backend creates the account
// and Keycloak mails the activation link. So the step lands in a state nothing on this console used to have
// — bound, mailed, unanswered — and §7.5.3 keeps the step `done` there deliberately, so steps 3 and 4 can be
// finished and the tenant can read as provisioned while the acceptance is outstanding.
//
// The console therefore has to say two things at once that a single badge cannot: the step is done, AND
// somebody is still expected to act. That is the `activation` detail, rendered as its own badge beside the
// invited account, carried up to the page's nav and the section's own badge (where "Valmis" alone would
// claim an onboarding nobody has accepted), and acted on by two controls the step did not have —
// "lähetä aktivointilinkki uudelleen" and "peru kutsu". Both act on the account the TENANT names and take no
// address, which is what makes a resend unable to create a second account.
//
// THE CANCEL ASKS FIRST, AND THE RESEND DOES NOT. A resend sends a message again; the worst a mis-click
// costs is a duplicate mail, and the link it carries is the same one. A cancel may DELETE a realm account
// — only when all three of §7.5.4's conditions hold, and only the server can test the third — so the
// confirmation says which of the two acts is the likely one for THIS account and reports which it actually
// was. Guessing in the dialog would be the one place this console could tell the operator an account was
// removed when it was not.
//
// STEP 3 IS A DOOR NOW, AND THE CONSOLE HAS TO SAY SO (task aa0c3eb7). Since `ApplicationEntitlementGate`
// (task b260fddb) a customer tenant without the `clms` code is served nothing on `/api` or `/scorm` — the
// pääkäyttäjä exactly as the learners — so unticking one checkbox here shuts the customer. Two things follow,
// and `tenantAccess.ts` holds the decision behind both:
//
//   * THE SAVE THAT CLOSES ACCESS ASKS FIRST, with the slug typed out, because that is the freeze's guard and
//     this is the freeze's consequence. Only that save: adding an entitlement, changing `hrm` (which gates
//     nothing) and re-saving an already-closed set go through untouched. The dialog names the freeze as the
//     path for a temporary shut, because the freeze carries the reason this request has no field for.
//   * AND THE QUESTION IS ASKED ABOUT THE SERVER'S STATE, NOT THIS PAGE'S. A confirmation decided on a
//     mounted read protects nothing on its own: a tab left open while another operator recorded step 3 shows
//     the wrong set, and the case that costs is a page showing a CLOSED set over a tenant somebody has since
//     opened — where pressing save looked like a no-op and would have shut that customer out unasked. So the
//     close path re-reads before it asks, and the WRITE carries the set it was decided over (`expected`) for
//     the server to refuse with a 409 if it has moved. The re-read narrows the window; the condition closes
//     it — and it is the replacing statement's own `WHERE` that does
//     (`TenantProvisioningService.ReplaceEntitlementsAsync`), so it holds for two saves that OVERLAP and
//     not only for one that is stale by the time it arrives. See `onSaveEntitlements`.
//   * THE CLOSED STATE IS STATED, not left as an unticked box — on this step, as a badge on the page's header
//     and nav (`TenantPage`), and on the customer DIRECTORY's row (`pages/TenantsPage.tsx`). In TWO forms,
//     because an empty set is two facts: nothing recorded yet (an unfinished onboarding) and recorded without
//     this application (somebody decided it, and the step dates it). It is said for a frozen tenant too,
//     where it is the fact a thaw does NOT fix: reopening the doors leaves a customer with no entitlement
//     exactly as shut as before.
//
// A FROZEN TENANT HAS NO FORMS HERE. The steps still render — an operator looking at a shut customer needs
// to see where its onboarding stood — but every one of them is read-only: step 2 would mail a person who
// cannot sign in, and step 4 would copy content into a tenant whose doors are all closed.
//
// WHY STEP 4 READS A SECOND ENDPOINT. The provisioning status says which library VERSIONS this tenant has
// been given, as ids — it deliberately does not carry their titles, because those are the customer's own
// course rows and this console would have to enter the tenant's RLS scope to read them. So the catalogue is
// read once from `/api/platform/content-library`, which is the library's own list and discloses nothing
// about any customer, and the ids are resolved against it. A version that is no longer in the catalogue
// (withdrawn after it was copied) renders as withdrawn rather than disappearing: the tenant still has it.

/**
 * What the close-access confirmation lists, as i18n keys (task aa0c3eb7). Shaped like the freeze's
 * `FREEZE_EFFECTS` and saying the same three kinds of thing — who is refused, what keeps running, and what
 * happens to the data — because the effects ARE the freeze's, which is the whole point the dialog has to make.
 *
 * Spelled out as literals rather than built from a loop, so `src/i18n/platform-i18n.test.mjs` can read them
 * out of this file and check both locales for them.
 */
const CLOSE_ACCESS_EFFECTS = [
  "tenants.provisioning.entitlements.closeEffectRequests",
  "tenants.provisioning.entitlements.closeEffectEveryone",
  "tenants.provisioning.entitlements.closeEffectData",
] as const;

/** What the section is doing. Each step's own in-flight state is separate — see `busyStep`. */
type Load =
  | { phase: "loading" }
  | { phase: "loadError" }
  | { phase: "ready"; provisioning: TenantProvisioning };

/**
 * The i18n key for a step-4 failure, by what the operator has to do about it.
 *
 * A 502 is the partial one: versions before the failure ARE in the tenant, so the message says the step
 * stopped rather than that it failed, and the status reload below shows what landed.
 *
 * TWO 502s, and only the `error` code tells them apart — the same arrangement step 2's two 409s use, for the
 * same reason. The ordinary one (`content_copy_failed`) stopped at a version the backend read back as absent,
 * so "copy the rest by sending the selection again" is the whole remedy. {@link CONTENT_COPY_UNKNOWN} stopped
 * at one whose outcome nobody established: the copy may be in the tenant, and telling the operator to retry
 * without telling them that would have them reading the step's own list as complete when it is not.
 */
function defaultContentErrorKey(err: unknown): string {
  if (!(err instanceof ApiError)) return "tenants.provisioning.defaultContent.errorNetwork";
  switch (err.status) {
    case 404:
      return "tenants.provisioning.defaultContent.error404";
    case 422:
      return "tenants.provisioning.defaultContent.error422";
    case 502:
      return err.problem?.error === CONTENT_COPY_UNKNOWN
        ? "tenants.provisioning.defaultContent.error502Unknown"
        : "tenants.provisioning.defaultContent.error502";
    default:
      return "tenants.provisioning.defaultContent.errorNetwork";
  }
}

/**
 * The whole provisioning status a REFUSAL carries as a problem-details extension, or null when it carries
 * none.
 *
 * Two refusals on this surface do, and both for the same reason: the state on screen is now wrong in a way
 * the operator has to act on, and re-reading it would append a `TENANT_ACCESSED` audit row (FR-Q.35) for a
 * crossing nobody asked for on a request that the server already answered with the facts. See
 * {@link partialProvisioning} and {@link conflictProvisioning} for which is which.
 */
function carriedProvisioning(err: unknown, status: number): TenantProvisioning | null {
  if (!(err instanceof ApiError) || err.status !== status) return null;
  const carried = err.problem?.provisioning;
  // Shape-checked on the one field the dialog cannot render without, rather than trusted: this is an
  // extension member, typed `unknown` on ProblemDetails for exactly that reason.
  return carried && typeof carried === "object" && Array.isArray((carried as TenantProvisioning).steps)
    ? (carried as TenantProvisioning)
    : null;
}

/**
 * The provisioning status a PARTIAL step 4 carries on its 502, or null when the failure carries none.
 *
 * The step's one outcome that is neither a success nor a no-op: a copy that stopped partway leaves the
 * earlier versions in the tenant, so the endpoint answers 502 with `provisioning` — the whole status after
 * what did land.
 */
function partialProvisioning(err: unknown): TenantProvisioning | null {
  return carriedProvisioning(err, 502);
}

/**
 * The status step 3's CONFLICT carries on its 409 — the set as it is now, after somebody else's answer
 * (task aa0c3eb7). Null for every other refusal, including a 409 from an older server that carries none.
 *
 * Nothing was written, so this is the current state and not a post-write one: what the operator has to
 * decide again is whether the save they were making still shuts this customer out.
 */
function conflictProvisioning(err: unknown): TenantProvisioning | null {
  return carriedProvisioning(err, 409);
}

/**
 * The server's own `detail` for a step-2 502, or undefined for every other outcome (task b64551c4).
 *
 * ONLY the 502, and that narrowness is the point. The other refusals have a remedy this console states in
 * the operator's own language, and repeating the server's English beside them would be noise. The 502 does
 * not: it is several different endings behind one status — the role was never granted, the role was granted
 * and taken back, or the role was granted and the compensating revoke failed too, leaving ADMIN on an
 * account with no tenant binding for somebody to remove by hand. Only the server saw which, and `detail`
 * is where it says so (`TenantProvisioningService.UndoAdminGrantAsync`).
 *
 * An empty/absent detail yields undefined rather than an empty string, so the console renders no quote at
 * all rather than an empty "the server said:" line.
 *
 * Since task 6f332099 this is the FALLBACK and no longer the first answer for the invite: a 502 that names
 * its compensation outcome gets `compensationRemedy` instead, which is the same ending in the operator's
 * own language. What still reaches this is a 502 carrying no code — the resend's and the cancel's, which
 * compensate nothing, and any ending a server older than that vocabulary reports.
 */
function serverDetail(err: unknown): string | undefined {
  if (!(err instanceof ApiError) || err.status !== 502) return undefined;
  const detail = err.problem?.detail;
  return typeof detail === "string" && detail.trim() !== "" ? detail.trim() : undefined;
}

/**
 * The i18n key for what a failed step 2 did about its own half-finished writes, or undefined when the
 * failure names no compensation outcome (task 6f332099).
 *
 * This is the half of a 502 that `error502` cannot carry. That sentence says the identity provider did not
 * answer and nobody was invited, which is true of all nine endings; what differs is what is LEFT BEHIND,
 * and therefore whether the retry is the whole fix or somebody has to open the Keycloak console. The server
 * is the only party that saw which — the compensation runs against the realm — so it names the ending as a
 * stable code and the console says it here, in both locales, rather than quoting the server's English on a
 * Finnish page.
 *
 * An outcome this build does not know still renders a sentence (`compensation.unknown`): a code added
 * server-side must not leave the one line that says whether anything needs cleaning up blank.
 */
function compensationRemedy(err: unknown): string | undefined {
  if (!(err instanceof ApiError) || err.status !== 502) return undefined;
  const outcome = err.problem?.compensation;
  return typeof outcome === "string" && outcome.trim() !== ""
    ? compensationKey(outcome.trim())
    : undefined;
}

/**
 * Is this the invite's `IdentityBoundElsewhere` 409 — the account is bound to ANOTHER tenant, which
 * ADR-0013 §6.3 does not let this step move?
 *
 * Recognised by the ABSENCE of the other 409's code, which is the arrangement the backend gives this
 * console rather than a choice made here: the malformed-attribute refusal carries
 * `identity_tenant_id_malformed` and this one carries no `error` at all (`PlatformTenantEndpoints`). So "no
 * code" is this refusal, and a 409 from an older server with no codes at all reads as this one too — which
 * is the safe direction, because its remedy is reversible (name somebody else, or use the role grant) where
 * the other's sends an operator into the Keycloak console to edit an attribute.
 *
 * It is asked in two places and the second is why it is a function: the MESSAGE comes from
 * {@link inviteErrorKey}, and since ADR-0026 §5 a LINK to the role-grant section comes with it. Both have
 * to be the same reading of the same response.
 */
function isBoundElsewhere(err: unknown): boolean {
  return (
    err instanceof ApiError
    && err.status === 409
    && err.problem?.error !== IDENTITY_TENANT_ID_MALFORMED
  );
}

/** The i18n key for an invite failure, by what the operator has to do about it. */
function inviteErrorKey(err: unknown): string {
  if (!(err instanceof ApiError)) return "tenants.provisioning.identity.errorNetwork";
  switch (err.status) {
    // The TENANT is gone — and that is now the only 404 this call can answer. It used to be "the realm has
    // no account for that address", which ADR-0014 §7.5 turned into the create path (6.10.2026): the
    // endpoint's remaining `IdentityNotFound` branch belongs to the RESEND, whose account can have been
    // erased since it was bound. A message still telling the operator to make the account by hand would
    // send them to the Keycloak console for a refusal that no longer exists.
    case 404:
      return "tenants.provisioning.identity.error404";
    // TWO refusals share this status, and only the `error` code tells them apart. Bound to another tenant
    // — ADR-0013 §6.3 does not let a person move — is fixed by naming somebody else. A tenant_id the
    // backend cannot read as a tenant is not: there is no other person to name, because the attribute
    // names nobody, and the only fix is to correct it in the Keycloak console. Branching on the status
    // alone printed the first remedy for both.
    case 409:
      return isBoundElsewhere(err)
        ? "tenants.provisioning.identity.error409"
        : "tenants.provisioning.identity.error409Malformed";
    case 422:
      return "tenants.provisioning.identity.error422";
    case 502:
      return "tenants.provisioning.identity.error502";
    default:
      return "tenants.provisioning.identity.errorNetwork";
  }
}

/**
 * The i18n key for a RESEND failure (ADR-0014 §7.5.4).
 *
 * Its own table and not the invite's, because the same statuses mean different things here and want
 * different things from the operator. The 404 is the one that inverts: on the invite it is a missing tenant,
 * on the resend it is a missing ACCOUNT — the invite's address was bound and the realm no longer holds it,
 * which no retry fixes and only a cancel-and-re-invite gets out of.
 *
 * The two 409s share a status and are told apart by the problem-details `error` code, exactly as step 2's
 * two conflicts are: "no invite here" is fixed by reloading, "they are already in" is not a failure at all.
 */
function resendErrorKey(err: unknown): string {
  if (!(err instanceof ApiError)) return "tenants.provisioning.identity.resendErrorNetwork";
  switch (err.status) {
    case 404:
      return "tenants.provisioning.identity.resendError404";
    case 409:
      return err.problem?.error === FIRST_ADMIN_SIGNED_IN
        ? "tenants.provisioning.identity.resendErrorSignedIn"
        : "tenants.provisioning.identity.errorNotInvited";
    case 502:
      return "tenants.provisioning.identity.resendError502";
    default:
      return "tenants.provisioning.identity.resendErrorNetwork";
  }
}

/**
 * The i18n key for a CANCEL failure (ADR-0014 §7.5.4).
 *
 * The signed-in 409 is the one that must not be collapsed into the other: an invite past cancelling is not
 * something to retry or reload for, because by then the person owns rows in the customer tenant — the
 * pääkäyttäjyys handover is the path, and that is a different section of this same page. The 502 is the
 * refusal where NOTHING was cleared, so the retry addresses the same account rather than orphaning it with
 * ADMIN; it says so, because "the cancel failed" alone would read as "the invite may be half-cancelled".
 */
function cancelErrorKey(err: unknown): string {
  if (!(err instanceof ApiError)) return "tenants.provisioning.identity.cancelErrorNetwork";
  switch (err.status) {
    case 409:
      return err.problem?.error === FIRST_ADMIN_NOT_INVITED
        ? "tenants.provisioning.identity.errorNotInvited"
        : "tenants.provisioning.identity.cancelErrorSignedIn";
    case 502:
      return "tenants.provisioning.identity.cancelError502";
    default:
      return "tenants.provisioning.identity.cancelErrorNetwork";
  }
}

/**
 * Which request is in flight. The STEP locks every other step's controls, and the ACTION is what step 2
 * needs on top of that: it has three buttons since §7.5.4, and only the pressed one may go busy — a
 * "Perutaan…" label on the resend button would be a lie about which request is out.
 */
type Busy = { step: string; action?: "invite" | "resend" | "cancel" };

/**
 * The close-access confirmation's own state (task aa0c3eb7), or null when no question is open. It carries
 * the freeze's typed slug for the freeze's reason, and not the freeze's reason field, which this request has
 * nowhere to put.
 *
 * NO `submitting` FLAG: the in-flight state is the section's `busy`, like the invite cancel's, because that
 * is the one the `finally` always clears. A copy kept here would stay true on the one path that returns
 * early — a `SessionExpiredError`, where the app is navigating away — and leave the dialog's button reading
 * "Suljetaan…" over a page that is going to the sign-in.
 */
type CloseDialog = { confirm: string; error: string | null };

export function TenantProvisioningSection({
  tenant,
  headingId,
  onChanged,
  onStatus,
}: {
  tenant: Tenant;
  /** The id of this section's own <h2> — the page owns the heading, this owns everything under it. */
  headingId: string;
  /**
   * A step landed. The page re-reads the tenant so the header's "provisioning unfinished" badge agrees
   * with what this section is showing — the badge is derived from the same facts, but it came from an
   * earlier read.
   */
  onChanged: () => void;
  /**
   * Where provisioning stands, for the page's section nav. Null until the status has been read (or after
   * the read failed), so the nav says nothing it does not know rather than guessing "unfinished".
   */
  onStatus: (provisioning: TenantProvisioning | null) => void;
}) {
  const { t } = useTranslation();
  /** A frozen tenant is read-only here: every form is withheld, not disabled. */
  const frozen = tenant.suspendedAt !== null;
  const [load, setLoad] = useState<Load>({ phase: "loading" });
  /**
   * The server's status, or null until it has been read. Derived up here rather than beside the render,
   * because the entitlements save reads the RECORDED set to decide whether its own write closes a door —
   * so it is a dependency of a callback and not only of JSX.
   */
  const provisioning = load.phase === "ready" ? load.provisioning : null;
  /** Which step — and for step 2, which of its buttons — has a request in flight. */
  const [busy, setBusy] = useState<Busy | null>(null);
  const busyStep = busy?.step ?? null;
  /** The error of the step that produced it, keyed by step code — one failure must not blank another. */
  const [errors, setErrors] = useState<Record<string, string>>({});
  /**
   * The SECOND line of a failure, beside the translated sentence: what was left behind and what to do about
   * it (tasks b64551c4, 6f332099). Already rendered — the caller resolves it, because its two sources are
   * not interchangeable. A 502 naming its compensation outcome yields a sentence from this console's own
   * locale files; one that names none yields the server's English, quoted as the server speaking.
   */
  const [details, setDetails] = useState<Record<string, string>>({});
  /** A step's own success note (above all "bound, but the mail did not leave"). */
  const [notes, setNotes] = useState<Record<string, string>>({});
  /**
   * The ONE refusal on this step that is answered somewhere else on this same page: the invite named an
   * address whose realm account is bound to another tenant, so ADR-0013 §6.3 will not move it here
   * (`IdentityBoundElsewhere`, 409).
   *
   * ADR-0026 §5 KEPT that refusal deliberately — this step's next write is a `tenant_id` bind, and ADR-0018
   * §5 forbids a `tenant_id` write that re-points an existing account — and what changed is that it stopped
   * being a dead end. Since §3 made `ADMIN` grantable, the role-grant section further down this page gives
   * exactly that person `ADMIN` in this customer, as a `tenant_memberships` row that binds nothing. So the
   * refusal gets a LINK to that section, and this flag is what carries the one case that may show it: the
   * surface's other 409 (an attribute naming no tenant) and its every other failure are not answered there
   * and must not point anybody at it.
   *
   * A boolean rather than a `Record` keyed by step, unlike the three states above it: only step 2 can
   * produce this refusal, and keying it by step would suggest otherwise.
   */
  const [boundElsewhere, setBoundElsewhere] = useState(false);

  const [email, setEmail] = useState("");
  /** The entitlement checkboxes. Seeded from the server on load and after every save. */
  const [chosen, setChosen] = useState<readonly string[]>([]);
  /**
   * The library catalogue, EVERY published version — not narrowed to the newest per item. Step 4 narrows it
   * where it offers versions, and resolves the tenant's applied ids against the whole thing; a copy of a
   * version Verso has since superseded is still in the library, and narrowing here would leave the step no
   * row to name it from. `null` while it is loading or after a failure: an empty ARRAY means "Verso has
   * published nothing", which the step renders as its own sentence, so the two must not be the same value.
   */
  const [library, setLibrary] = useState<ContentLibraryItem[] | null>(null);
  /** Which library versions the operator has ticked for this run. */
  const [contentPicks, setContentPicks] = useState<readonly string[]>([]);
  /** Is the cancel's confirmation open? The cancel is the one act here that can remove a realm account. */
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  /** The close-access question, open only for the step-3 save that shuts the customer (task aa0c3eb7). */
  const [closing, setClosing] = useState<CloseDialog | null>(null);
  /** The cancel's own refusal, shown in the dialog that provoked it rather than on the page behind it. */
  const [cancelError, setCancelError] = useState<string | null>(null);

  const emailRef = useRef<HTMLInputElement>(null);

  // The audited read, once, when the page opens this tenant. Aborted if the operator navigates away
  // before it lands.
  useEffect(() => {
    const controller = new AbortController();
    getProvisioning(tenant.id, controller.signal)
      .then((provisioning) => {
        setLoad({ phase: "ready", provisioning });
        setChosen(provisioning.entitlements);
        onStatus(provisioning);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || err instanceof SessionExpiredError) return;
        setLoad({ phase: "loadError" });
        onStatus(null);
      });
    return () => controller.abort();
  }, [tenant.id, onStatus]);

  // The library catalogue — a SEPARATE read, and separately recoverable. It discloses nothing about any
  // customer (the library is Verso's), so it is not audited and does not depend on the tenant; a failure
  // here leaves the other three steps usable rather than blanking the dialog.
  useEffect(() => {
    const controller = new AbortController();
    listContentLibrary(controller.signal)
      .then((items) => setLibrary(items))
      .catch((err: unknown) => {
        if (controller.signal.aborted || err instanceof SessionExpiredError) return;
        setLibrary(null);
      });
    return () => controller.abort();
  }, []);

  /** A step succeeded: adopt the server's whole status, clear that step's error, tell the page. */
  const landed = useCallback(
    (step: string, provisioning: TenantProvisioning, note?: string) => {
      setLoad({ phase: "ready", provisioning });
      setChosen(provisioning.entitlements);
      setErrors((previous) => ({ ...previous, [step]: "" }));
      setDetails((previous) => ({ ...previous, [step]: "" }));
      setNotes((previous) => ({ ...previous, [step]: note ?? "" }));
      setBoundElsewhere(false);
      onStatus(provisioning);
      onChanged();
    },
    [onChanged, onStatus],
  );

  /**
   * Adopt a status the server has given us when NOTHING OF OURS LANDED (task aa0c3eb7) — the close path's
   * re-read, and the conflict's carried status. Two differences from {@link landed}, and both follow from
   * that:
   *
   *   * the TENANT is not re-read. `onChanged` exists so the page's header agrees with a step that just
   *     wrote something; here the row has not moved, and the re-read is itself audited
   *     (`TENANT_ACCESSED`, FR-Q.35) — a refused write must not file a crossing.
   *   * the SELECTION is left alone. The caller reseeds it when the server's answer has changed, because
   *     the operator's edit was made over a state that no longer exists; on the close path's unchanged
   *     re-read it must NOT be touched, or the box they just unticked is silently ticked again and the
   *     confirmation goes up for a save that removes nothing.
   */
  const adopted = useCallback(
    (fresh: TenantProvisioning) => {
      setLoad({ phase: "ready", provisioning: fresh });
      onStatus(fresh);
    },
    [onStatus],
  );

  const failed = useCallback((step: string, message: string, detail?: string) => {
    setErrors((previous) => ({ ...previous, [step]: message }));
    setDetails((previous) => ({ ...previous, [step]: detail ?? "" }));
    setNotes((previous) => ({ ...previous, [step]: "" }));
    // Cleared by EVERY failure and raised again by the one that earns it (see `boundElsewhere`), so the
    // link to the role-grant section cannot outlive the refusal it answers — a message saying "fix the
    // tenant_id attribute in Keycloak" with "grant them ADMIN below" still under it would offer a remedy
    // for the previous attempt.
    setBoundElsewhere(false);
  }, []);

  /**
   * The server's own words as the failure's second line, marked up as a QUOTE ("the server reported: …")
   * because they are English on a page that may be Finnish. The fallback for a 502 that names no
   * compensation outcome; one that names it gets this console's own sentence instead, unquoted, because the
   * console is then the speaker (task 6f332099).
   */
  const serverDetailLine = useCallback(
    (err: unknown) => {
      const detail = serverDetail(err);
      return detail ? t("tenants.provisioning.serverDetail", { detail }) : undefined;
    },
    [t],
  );

  const onInvite = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (busyStep) return;

      const address = email.trim();
      if (address.length === 0) {
        failed("identity", t("tenants.provisioning.identity.emailRequired"));
        return;
      }

      setBusy({ step: "identity", action: "invite" });
      inviteFirstAdministrator(tenant.id, { email: address })
        .then((invite) => {
          // A 200 whose mail did not leave is a SUCCESSFUL step with one thing left to retry: the person is
          // bound. Saying "invite failed" here would send the operator to re-bind somebody already bound,
          // and saying nothing would have them wait for a reply to a message nobody got.
          //
          // And a 200 whose mail DID leave is now two different successes, because ADR-0014 §7.5.1 made the
          // unknown address the create path: either an account that already existed was bound and got
          // CLMS's invite, or Verso made the account and KEYCLOAK sent an activation link the person has to
          // follow before they can sign in at all. Reporting the second as the first would leave the
          // operator waiting for somebody who is waiting for a link they were not told about — so the note
          // branches on the server's own `accountCreated`, which is the one fact §7.5.3 records rather than
          // derives.
          landed(
            "identity",
            invite.provisioning,
            invite.mail !== MAIL_SENT
              ? t("tenants.provisioning.identity.invitedNoMail")
              : invite.provisioning.firstAdministrator?.accountCreated
                ? t("tenants.provisioning.identity.invitedCreated")
                : t("tenants.provisioning.identity.invited"),
          );
          setEmail("");
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          // Task b64551c4 — the 502 is the one refusal whose REMEDY the console cannot derive. The step
          // grants the ADMIN client role before it writes the tenant binding, and when the binding fails it
          // reads the realm back and decides: leave the role (the write landed after all), take it back, or
          // leave it and say so because the realm could not be re-read. If the compensating revoke itself
          // fails the account is left holding ADMIN with no tenant_id and somebody has to remove it by hand
          // in the Keycloak console. Only the server saw which ending happened.
          //
          // Task 6f332099 — and now it NAMES it, so the console states the remedy in the operator's own
          // language. b64551c4 quoted the server's English instead, because paraphrasing prose would have
          // meant guessing which ending it described; a stable code is not prose, and the guess goes away.
          // The verbatim quote stays as the fallback for a 502 that carries no code.
          const remedy = compensationRemedy(err);
          failed(
            "identity",
            t(inviteErrorKey(err)),
            remedy ? t(remedy) : serverDetailLine(err),
          );
          // ADR-0026 §5 — the one refusal here whose remedy is further down THIS page. `failed` has just
          // cleared the flag, so this is the only way it goes up.
          if (isBoundElsewhere(err)) setBoundElsewhere(true);
        })
        .finally(() => setBusy(null));
    },
    [busyStep, email, tenant.id, landed, failed, serverDetailLine, t],
  );

  /**
   * Send the bound administrator their invite again (ADR-0014 §7.5.4). No address: it acts on the account
   * the TENANT names, which is what makes it unable to create a second one.
   */
  const onResend = useCallback(() => {
    if (busyStep) return;

    setBusy({ step: "identity", action: "resend" });
    resendFirstAdminActivation(tenant.id)
      .then((invite) =>
        // The same three outcomes the invite has, for the same reason: the mail is the half that can fail
        // on its own, and which mail went out depends on whether this console made the account.
        landed(
          "identity",
          invite.provisioning,
          invite.mail !== MAIL_SENT
            ? t("tenants.provisioning.identity.resentNoMail")
            : invite.provisioning.firstAdministrator?.accountCreated
              ? t("tenants.provisioning.identity.resentActivation")
              : t("tenants.provisioning.identity.resentInvite"),
        ),
      )
      .catch((err: unknown) => {
        if (err instanceof SessionExpiredError) return;
        failed("identity", t(resendErrorKey(err)), serverDetailLine(err));
      })
      .finally(() => setBusy(null));
  }, [busyStep, tenant.id, landed, failed, serverDetailLine, t]);

  /**
   * Cancel the invite (ADR-0014 §7.5.4). The server decides whether the realm account is deleted or merely
   * unbound, and the note reports which — the console must not claim an account was removed when it was not.
   *
   * The confirmation closes only on success, and a refusal is reported INSIDE it — the step's own error
   * line sits on the page behind the dialog, where an operator who had just answered a confirmation would
   * not see it.
   */
  const onCancelInvite = useCallback(() => {
    if (busyStep) return;

    setCancelError(null);
    setBusy({ step: "identity", action: "cancel" });
    cancelFirstAdminInvite(tenant.id)
      .then((cancelled) => {
        setConfirmingCancel(false);
        landed("identity", cancelled.provisioning, t(cancelOutcomeKey(cancelled.account)));
        // Step 2 is pending again, so its field is back — and the keyboard goes to it rather than to the
        // button that has just disappeared with the invite it acted on.
        queueMicrotask(() => emailRef.current?.focus());
      })
      .catch((err: unknown) => {
        if (err instanceof SessionExpiredError) return;
        setCancelError(t(cancelErrorKey(err)));
      })
      .finally(() => setBusy(null));
  }, [busyStep, tenant.id, landed, t]);

  /**
   * Step 3's write, shared by the plain save and the confirmed one.
   *
   * `confirmed` decides only WHERE a refusal is reported: a dialog left standing over the step's own error
   * line would hide the sentence the operator has to act on, so a confirmed save reports inside the dialog it
   * came from, exactly as the invite cancel does.
   *
   * The success note branches on what the SERVER came back with against what it held before, not on what the
   * boxes said: the endpoint normalises the set, and "the entitlements were saved" is precisely the sentence
   * that used to hide a customer being shut out — so it is kept for the saves that are only bookkeeping.
   */
  const saveEntitlements = useCallback(
    (confirmed: boolean) => {
      const before = provisioning?.entitlements ?? [];

      setBusy({ step: "entitlements" });
      if (confirmed) setClosing((d) => d && { ...d, error: null });
      // `expected` is the set this decision was made over, and the server REFUSES a replace without it
      // (task aa0c3eb7). It is the same list the step rendered its boxes from, so what the operator saw and
      // what the condition is checked against cannot come apart.
      setEntitlements(tenant.id, { entitlements: [...chosen], expected: [...before] })
        .then((saved) => {
          setClosing(null);
          landed(
            "entitlements",
            saved,
            t(
              closesAccess(before, saved.entitlements)
                ? "tenants.provisioning.entitlements.closed"
                : opensAccess(before, saved.entitlements)
                  ? "tenants.provisioning.entitlements.opened"
                  : "tenants.provisioning.entitlements.saved",
            ),
          );
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;

          // THE CONFLICT (task aa0c3eb7). Somebody else answered step 3 between the read this page is
          // showing and this write, so nothing was recorded — including, in the case this whole guard is
          // about, a close that would have shut a customer the operator believed was already shut.
          //
          // It is reported on the STEP and the dialog is dismissed, even though every other refusal of a
          // confirmed save is reported inside the dialog: the dialog's question was about a state that no
          // longer exists, and leaving it standing over refreshed facts would invite the operator to
          // confirm it a second time without having seen what changed. The server's set is adopted from the
          // refusal itself — no re-read, because nothing was written and `GET …/provisioning` would file a
          // TENANT_ACCESSED row for a crossing nobody asked for — the boxes are reset to it, and the next
          // submit decides again over what is really there.
          if (
            err instanceof ApiError
            && err.status === 409
            && err.problem?.error === ENTITLEMENTS_CHANGED_CONCURRENTLY
          ) {
            setClosing(null);
            const current = conflictProvisioning(err);
            if (current) {
              adopted(current);
              setChosen(current.entitlements);
            }
            failed("entitlements", t("tenants.provisioning.entitlements.errorChanged"));
            return;
          }

          const message = t(
            err instanceof ApiError && err.status === 422
              ? "tenants.provisioning.entitlements.error422"
              : "tenants.provisioning.entitlements.errorNetwork",
          );
          if (confirmed) setClosing((d) => d && { ...d, error: message });
          else failed("entitlements", message);
        })
        .finally(() => setBusy(null));
    },
    [provisioning?.entitlements, chosen, tenant.id, landed, adopted, failed, t],
  );

  /**
   * The step-3 submit. One save out of all of them is a door, and it is the one that asks first (task
   * aa0c3eb7): a selection that drops `clms` from a tenant that HAS it shuts every `/api` and `/scorm`
   * request that customer makes.
   *
   * WHICH MAKES THE PAGE'S OWN READ THE WRONG THING TO JUDGE IT ON, and that is the correction this round
   * makes. `provisioning.entitlements` is what the server said when this section mounted, so a page left
   * open while another operator answered step 3 asks — or does NOT ask — the wrong question: the case that
   * matters is a page showing a CLOSED set over a tenant somebody has since opened, where unticking nothing
   * and pressing save looked like a no-op and would have shut that customer out without a word. So:
   *
   *   * the close path RE-READS the status before it asks anything, and if the answer has moved it reports
   *     what is there instead of putting a confirmation in front of the operator over a state that is gone;
   *   * and the write itself is conditional on the set it was decided over, which is what actually
   *     guarantees it (`setEntitlements`' `expected`, refused with a 409 the server reaches from its
   *     replacing statement's own `WHERE`, so two saves that overlap cannot both pass). The re-read
   *     narrows the window; only the server can close it.
   *
   * The extra read is audited (`TENANT_ACCESSED`, FR-Q.35) and that is the right side of the trade: it
   * happens when an operator is about to shut a customer out, which is a crossing worth a row, and not once
   * per tenant anybody scrolls past.
   */
  const onSaveEntitlements = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (busyStep) return;

      const before = provisioning?.entitlements ?? [];
      if (!closesAccess(before, chosen)) {
        // Not a close as far as this page knows — which the server checks for itself: if this page is stale
        // and the save really would close a door, `expected` no longer matches and the 409 refuses it.
        saveEntitlements(false);
        return;
      }

      setBusy({ step: "entitlements" });
      getProvisioning(tenant.id)
        .then((fresh) => {
          adopted(fresh);
          if (!sameEntitlements(before, fresh.entitlements)) {
            // Stopped deliberately rather than asking about the fresh state in the same breath: the
            // operator has to SEE what somebody else recorded before deciding again — so the boxes are
            // reseeded from the server and their pending edit, which was made over a state that no longer
            // exists, goes with it.
            setChosen(fresh.entitlements);
            failed("entitlements", t("tenants.provisioning.entitlements.errorChanged"));
            return;
          }
          // Unchanged: the SELECTION is left alone — it is the edit this confirmation is about.
          setClosing({ confirm: "", error: null });
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          // No confirmation over a state that could not be refreshed. The save is not attempted either:
          // this is the one press where being out of date is the whole risk.
          failed("entitlements", t("tenants.provisioning.entitlements.errorRecheck"));
        })
        .finally(() => setBusy(null));
    },
    [busyStep, provisioning?.entitlements, chosen, tenant.id, adopted, failed, saveEntitlements, t],
  );

  const onApplyDefaultContent = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (busyStep) return;

      if (contentPicks.length === 0) {
        failed("default_content", t("tenants.provisioning.defaultContent.pickRequired"));
        return;
      }

      setBusy({ step: "default_content" });
      applyDefaultContent(tenant.id, { libraryItemIds: [...contentPicks] })
        .then((result) => {
          // Both outcomes are successes and they do not mean the same thing: `already_applied` is the
          // retry, and reporting it as "copied" would have an operator believe a second course exists.
          const copied = result.items.filter((item) => item.outcome === CONTENT_APPLIED).length;
          landed(
            "default_content",
            result.provisioning,
            copied === 0
              ? t("tenants.provisioning.defaultContent.alreadyThere", { count: result.items.length })
              : t("tenants.provisioning.defaultContent.applied", { count: copied }),
          );
          // The ticks are cleared because the step's own list now shows what the tenant has; leaving them
          // on would invite a second press that reports every one of them as already applied.
          setContentPicks([]);
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          failed("default_content", t(defaultContentErrorKey(err)));

          // A 502 means the step stopped PARTWAY: the versions before the failure ARE in the tenant, so the
          // status on screen is now stale in the one direction that matters. The 502 CARRIES the status
          // after the partial step, so it is adopted from the problem body rather than re-read — a second
          // `GET …/provisioning` would append a `TENANT_ACCESSED` audit row (FR-Q.35) recording a crossing
          // the operator never asked for, on every failed copy.
          const partial = partialProvisioning(err);
          if (partial) {
            setLoad({ phase: "ready", provisioning: partial });
            onStatus(partial);
          }
        })
        .finally(() => setBusy(null));
    },
    [busyStep, contentPicks, tenant.id, landed, failed, onStatus, t],
  );

  const pending = provisioning?.steps.filter((step) => step.state === STEP_PENDING).length ?? 0;
  /**
   * Every step that can be done IS done, and the invited administrator still has not followed their
   * activation link (ADR-0014 §7.5.3). Its own badge, because the two things the operator needs from this
   * one are in tension: there is nothing left for THEM to do, and the onboarding is not accepted. "Valmis"
   * alone said the first and hid the second, and a pending-step badge would say the opposite of both.
   */
  const awaitingActivation =
    provisioning !== null &&
    provisioning.complete &&
    provisioning.firstAdministrator?.activation === ACTIVATION_AWAITING_ACTIVATION;

  return (
    <>
      <div className="tenant-section__head">
        <h2 id={headingId}>{t("tenants.provisioning.sectionTitle")}</h2>
        {provisioning &&
          (awaitingActivation ? (
            <span className="badge badge--status-in_progress" data-testid="provisioning-badge">
              {t("tenants.provisioning.badgeAwaitingActivation")}
            </span>
          ) : provisioning.complete ? (
            <span className="badge badge--user-active" data-testid="provisioning-badge">
              {t("tenants.provisioning.badgeComplete")}
            </span>
          ) : (
            <span className="badge badge--status-not_started" data-testid="provisioning-badge">
              {t("tenants.provisioning.badgePending", { count: pending })}
            </span>
          ))}
      </div>

      {/* ADR-0013 §5's true half — creating a tenant writes nothing into the realm — followed by who does
          write: step 2 itself, on the server's service account (ADR-0014 §7.5.1). The note used to end by
          telling the operator to set `tenant_id` and ADMIN by hand in the Keycloak console, from the runbook
          that predates step 2; that asked for the two writes the form below this paragraph performs. */}
      <p className="provisioning__intro" role="note" data-testid="provisioning-note">
        {t("tenants.provisioningNote")}
      </p>

      {/* The read is section-level, so one failed status read leaves the rest of the page usable. */}
      {load.phase === "loading" && (
        <p className="empty-state" role="status" aria-live="polite">
          {t("tenants.provisioning.loading")}
        </p>
      )}

      {load.phase === "loadError" && (
        <p className="banner banner--error" role="alert" data-testid="provisioning-load-error">
          {t("tenants.provisioning.loadError")}
        </p>
      )}

      {provisioning && (
        <>
          {/* The one-line answer to "is this customer onboarded", so it does not have to be read off four
              badges. `complete` is the server's derivation — see TenantProvisioningState. */}
          <p className="provisioning__summary" data-testid="provisioning-summary">
            {awaitingActivation
              ? t("tenants.provisioning.summaryAwaitingActivation")
              : provisioning.complete
                ? t("tenants.provisioning.summaryComplete")
                : t("tenants.provisioning.summaryIncomplete")}
          </p>

          <ol className="provisioning__steps" data-testid="provisioning-steps">
            {provisioning.steps.map((step, index) => (
              <li
                key={step.step}
                className="provisioning__step"
                data-testid={`provisioning-step-${step.step}`}
                data-state={step.state}
                data-last={index === provisioning.steps.length - 1 ? "" : undefined}
              >
                {/* The marker column: the step's number, or a tick once it is done, with the connector
                    running down to the next step. `aria-hidden` because the state is already a labelled
                    badge beside the title and the number is already the <ol>'s own ordinal. */}
                <span className="provisioning__marker" data-state={step.state} aria-hidden="true">
                  {step.state === STEP_DONE ? "✓" : index + 1}
                </span>

                <div className="provisioning__step-main">
                  <h3 className="provisioning__step-title">{t(stepTitleKey(step.step))}</h3>
                  <span
                    className={`badge ${stateBadgeClass(step.state)}`}
                    data-testid={`provisioning-state-${step.step}`}
                  >
                    {t(stepStateKey(step.state))}
                  </span>
                  <p className="provisioning__step-body">{t(stepBodyKey(step.step))}</p>

                  {notes[step.step] && (
                    <p
                      className="banner banner--success"
                      role="status"
                      data-testid={`provisioning-note-${step.step}`}
                    >
                      {notes[step.step]}
                    </p>
                  )}
                  {errors[step.step] && (
                    <p
                      className="field-error"
                      role="alert"
                      data-testid={`provisioning-error-${step.step}`}
                    >
                      {errors[step.step]}
                    </p>
                  )}
                  {/* What the failed step left behind and what to do about it, when the translated sentence
                      above cannot carry it (tasks b64551c4, 6f332099). Resolved by whoever set it: this
                      console's own sentence for a failure that named its compensation outcome, and the
                      server's quoted English for one that named none. Its own paragraph either way, because
                      it is the line an operator acts on. */}
                  {details[step.step] && (
                    <p
                      className="provisioning__server-detail"
                      data-testid={`provisioning-detail-${step.step}`}
                    >
                      {details[step.step]}
                    </p>
                  )}

                  {/* ADR-0026 §5 — WHERE TO GO, for the one refusal on this page that another section of
                      the same page answers. The account the operator named is bound to another tenant, so
                      this step cannot have it: its next write is a `tenant_id` bind and ADR-0018 §5 forbids
                      re-pointing an account, which is why §5 keeps the refusal exactly as it is. What it
                      stopped being is a dead end — since §3 made `ADMIN` grantable, "Roolit tässä
                      asiakkaassa" gives that same person `ADMIN` here, as a membership row that binds
                      nothing.

                      A MESSAGE AND A LINK, and no mechanism: no grant route is added to this step. The
                      alternative — telling the operator in prose to scroll down — is what the refusal
                      already did, and the section it means is below the fold on any tenant page.

                      A real `<a href="#…">` to the section's own id, which is the page's anchor target
                      (`TenantPage`'s `Section`), so the keyboard, the middle click and the browser's back
                      all work. Its own paragraph and outside the error's `role="alert"`, because a live
                      region is announced as text and a link inside one is handed to a reader as words they
                      cannot reach; here it follows the announcement as an ordinary control. */}
                  {step.step === "identity" && boundElsewhere && (
                    <p
                      className="provisioning__step-body"
                      data-testid="provisioning-identity-bound-elsewhere"
                    >
                      <a href="#tenant-section-roles" data-testid="provisioning-identity-roles-link">
                        {t("tenants.provisioning.identity.error409Roles")}
                      </a>
                    </p>
                  )}

                  {step.step === "identity" && (
                    <IdentityStep
                      step={step}
                      provisioning={provisioning}
                      email={email}
                      onEmailChange={setEmail}
                      emailRef={emailRef}
                      action={busy?.step === "identity" ? (busy.action ?? null) : null}
                      disabled={busyStep !== null}
                      frozen={frozen}
                      onSubmit={onInvite}
                      onResend={onResend}
                      onCancelOpen={() => {
                        setCancelError(null);
                        setConfirmingCancel(true);
                      }}
                    />
                  )}

                  {/* Step 2's OTHER half (§7.2, §12 C-6) — beside the invite rather than as a fifth step,
                      because a customer with no identity provider of their own is not half-provisioned.
                      `landed` under its own key, so a federation save does not blank the invite's note or
                      inherit its error. */}
                  {step.step === "identity" && (
                    <TenantFederationForm
                      tenantId={tenant.id}
                      federation={provisioning.federation}
                      disabled={busyStep !== null}
                      frozen={frozen}
                      onChanged={(updated) => landed("federation", updated)}
                    />
                  )}

                  {step.step === "entitlements" && (
                    <EntitlementsStep
                      recorded={provisioning.entitlements}
                      recordedAt={provisioning.entitlementsRecordedAt}
                      chosen={chosen}
                      onToggle={(code, on) =>
                        setChosen((previous) =>
                          on
                            ? [...previous.filter((c) => c !== code), code]
                            : previous.filter((c) => c !== code),
                        )
                      }
                      busy={busyStep === "entitlements"}
                      disabled={busyStep !== null}
                      frozen={frozen}
                      onSubmit={onSaveEntitlements}
                    />
                  )}

                  {step.step === "default_content" && (
                    <DefaultContentStep
                      library={library}
                      applied={provisioning.defaultContentItemIds}
                      picks={contentPicks}
                      onToggle={(id, on) =>
                        setContentPicks((previous) =>
                          on
                            ? [...previous.filter((p) => p !== id), id]
                            : previous.filter((p) => p !== id),
                        )
                      }
                      busy={busyStep === "default_content"}
                      disabled={busyStep !== null}
                      frozen={frozen}
                      onSubmit={onApplyDefaultContent}
                    />
                  )}
                </div>
              </li>
            ))}
          </ol>
        </>
      )}

      {confirmingCancel && provisioning?.firstAdministrator && (
        <CancelInviteDialog
          accountCreated={provisioning.firstAdministrator.accountCreated}
          error={cancelError}
          busy={busy?.step === "identity" && busy.action === "cancel"}
          onConfirm={onCancelInvite}
          // No focus handling here: `Modal` hands the keyboard back to the control that opened it, which is
          // the "Peru kutsu" button and is still on screen after a dismissal. A successful cancel is the
          // case that needs help, and `onCancelInvite` does it — that button goes away with the invite.
          onClose={() => setConfirmingCancel(false)}
        />
      )}

      {/* The step-3 save that shuts the customer (task aa0c3eb7). No focus handling, like the cancel's: the
          "Tallenna sovellusoikeudet" button that opened it is still on screen after a dismissal, and `Modal`
          hands the keyboard back to it. */}
      {closing && (
        <CloseAccessDialog
          slug={tenant.slug}
          typed={closing.confirm}
          onTyped={(value) => setClosing((d) => d && { ...d, confirm: value })}
          error={closing.error}
          busy={busy?.step === "entitlements"}
          onConfirm={() => saveEntitlements(true)}
          onClose={() => setClosing(null)}
        />
      )}
    </>
  );
}

/** The badge tone of a step state. Reuses the table's existing status badges rather than inventing three. */
function stateBadgeClass(state: string): string {
  if (state === STEP_PENDING) return "badge--status-not_started";
  if (state === STEP_NOT_AVAILABLE) return "badge--status-in_progress";
  return "badge--user-active";
}

/**
 * Step 2 — the first administrator's invite. The address field is offered whether or not the step is done,
 * because §7.1 makes every step separately RETRYABLE: naming an address again re-asserts both identity
 * writes (which change nothing) and sends a fresh mail.
 *
 * SINCE ADR-0014 §7.5 IT CARRIES TWO MORE CONTROLS, and they are not the field's retry. Re-submitting the
 * FIELD aims at whatever address is typed in it, which is how an operator corrects a wrong one; the resend
 * and the cancel aim at the account the TENANT names and take no address at all. Keeping them apart is what
 * stops "send it again" from quietly binding somebody else because a stale address was still in the box.
 *
 * BOTH ARE WITHHELD ONCE THE PERSON HAS SIGNED IN, which is the backend's own gate (§7.5.4: the refusal is
 * the `users` row, not the activation display state — a Keycloak blip must not withhold the one remedy the
 * operator has). Withheld and not disabled, with the handover named in their place: by then the person owns
 * rows in the customer tenant, and the pääkäyttäjyys section of this same page is the path.
 */
function IdentityStep({
  step,
  provisioning,
  email,
  onEmailChange,
  emailRef,
  action,
  disabled,
  frozen,
  onSubmit,
  onResend,
  onCancelOpen,
}: {
  step: TenantProvisioningStep;
  provisioning: TenantProvisioning;
  email: string;
  onEmailChange: (value: string) => void;
  emailRef: React.RefObject<HTMLInputElement | null>;
  /** Which of this step's three requests is in flight, so only that button's label changes. */
  action: "invite" | "resend" | "cancel" | null;
  disabled: boolean;
  /** The tenant is frozen: what is bound still shows, the invite form does not. */
  frozen: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onResend: () => void;
  onCancelOpen: () => void;
}) {
  const { t } = useTranslation();
  const bound = provisioning.firstAdministrator;

  /* What is bound, and how far that person has got. No name and no address: the identity's PII lives in
     Keycloak and the platform deliberately stores none of it (see TenantFirstAdministratorDto), so what
     the console can honestly report is the subject, the date, and the activation state.

     The state is a BADGE and a sentence, not one line of prose. The badge is the fact an operator scanning
     the stepper needs — "kutsuttu, odottaa aktivointia" is the state of a tenant whose every step reads
     done — and the sentence is what they can do about it, which differs in all three. */
  const boundState = bound && (
    <>
      <p className="provisioning__bound" data-testid="provisioning-bound">
        {t("tenants.provisioning.identity.bound", {
          subject: bound.subject,
          date: formatDate(new Date(bound.invitedAt)),
        })}
      </p>
      <p className="provisioning__bound-state" data-testid="provisioning-bound-signed-in">
        <span
          className={`badge ${bound.hasSignedIn ? "badge--user-active" : "badge--status-in_progress"}`}
          data-testid="provisioning-activation"
        >
          {t(activationKey(bound.activation))}
        </span>{" "}
        {t(activationBodyKey(bound.activation))}
      </p>
    </>
  );

  // A frozen tenant keeps the FACTS and loses the form: the invite mail would point a person at a sign-in
  // every door of that tenant refuses, so offering it would be offering a message that cannot be acted on.
  // The resend and the cancel go with it — a resend is that same message, and a cancel on a shut customer
  // is an identity write nobody has asked for.
  if (frozen) {
    return (
      <div className="provisioning__form">
        {boundState}
        <p className="provisioning__hint" role="note" data-testid="provisioning-frozen-identity">
          {t("tenants.provisioning.frozenReadOnly")}
        </p>
      </div>
    );
  }

  return (
    // `noValidate`, with the input still `type="email"` for the keyboard and the a11y semantics: the
    // browser's own constraint check would refuse to submit a malformed address with a tooltip, so the
    // operator would never see this app's message — the same reason every other form on this console keeps
    // its required-field checks in the submit handler. The backend validates it regardless (422).
    <form className="form provisioning__form" onSubmit={onSubmit} noValidate>
      {boundState}

      {/* The two acts on the invite that is already out (§7.5.4), ABOVE the address field and in their own
          row: they are what an operator chasing a stalled onboarding came for, and putting them under the
          field would read as two more ways to submit it. */}
      {bound && !bound.hasSignedIn && (
        <div className="provisioning__invite-actions" data-testid="provisioning-invite-actions">
          <button
            type="button"
            className="btn"
            onClick={onResend}
            disabled={disabled}
            data-testid="provisioning-resend"
          >
            {action === "resend"
              ? t("tenants.provisioning.identity.resending")
              : // An account this console created has no credential, so its only mail is Keycloak's
                // activation link; one it found and bound gets CLMS's invite instead (§7.5.1's table). The
                // button says which, because "aktivointilinkki" to somebody who already has a password
                // would describe a mail they will not receive.
                bound.accountCreated
                ? t("tenants.provisioning.identity.resendActivation")
                : t("tenants.provisioning.identity.resendInvite")}
          </button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={onCancelOpen}
            disabled={disabled}
            data-testid="provisioning-cancel-open"
          >
            {t("tenants.provisioning.identity.cancelInvite")}
          </button>
        </div>
      )}

      {/* Signed in: neither act is offered, and the reason is named rather than left as an absence. */}
      {bound?.hasSignedIn && (
        <p className="provisioning__hint" role="note" data-testid="provisioning-handover-note">
          {t("tenants.provisioning.identity.signedInHandover")}
        </p>
      )}

      <label htmlFor="provisioning-email">{t("tenants.provisioning.identity.emailLabel")}</label>
      {/* No native `required`: the check is in the submit handler so the form shows the app's own message
          rather than a browser tooltip, as every other form on this console does. */}
      <input
        id="provisioning-email"
        ref={emailRef}
        type="email"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        autoComplete="off"
        aria-describedby="provisioning-email-hint"
        data-testid="provisioning-email"
      />
      <p id="provisioning-email-hint" className="provisioning__hint">
        {t("tenants.provisioning.identity.emailHint")}
      </p>

      <div className="provisioning__step-actions">
        <button
          type="submit"
          className="btn btn--primary"
          disabled={disabled}
          data-testid="provisioning-invite-submit"
        >
          {action === "invite"
            ? t("tenants.provisioning.identity.inviting")
            : step.state === STEP_PENDING
              ? t("tenants.provisioning.identity.invite")
              : t("tenants.provisioning.identity.reinvite")}
        </button>
      </div>
    </form>
  );
}

/**
 * The cancel's confirmation (ADR-0014 §7.5.4) — an `alertdialog`, because it is a question that has to be
 * answered and not a form to fill in.
 *
 * WHAT IT PROMISES IS WHAT THE SERVER CAN ACTUALLY DELIVER, which is the whole reason the body branches.
 * The delete needs all three of §7.5.4's conditions and the third — no credential on the account — is a
 * Keycloak read only the server makes, so an account this console created is one the cancel will PROBABLY
 * delete and not one it certainly will: if the person has already set a password the non-destructive branch
 * runs instead. An account the console merely found is never deleted at all. Promising a deletion here and
 * then unbinding, or the reverse, would make this dialog the one place the console lies about an identity
 * write — so it states the likely outcome as the likely one, and the note after the request reports which
 * actually happened.
 */
function CancelInviteDialog({
  accountCreated,
  error,
  busy,
  onConfirm,
  onClose,
}: {
  /** Whether THIS console created the realm account — §7.5.3's one recorded fact, and the branch below. */
  accountCreated: boolean;
  error: string | null;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const titleId = "provisioning-cancel-title";
  const bodyId = "provisioning-cancel-body";

  return (
    <Modal
      role="alertdialog"
      className="provisioning__cancel-dialog"
      labelledBy={titleId}
      describedBy={bodyId}
      testId="provisioning-cancel-dialog"
      onClose={onClose}
      closeDisabled={busy}
    >
      <h2 id={titleId}>{t("tenants.provisioning.identity.cancelConfirmTitle")}</h2>
      <div id={bodyId}>
        <p>{t("tenants.provisioning.identity.cancelConfirmBody")}</p>
        <p data-testid="provisioning-cancel-account">
          {accountCreated
            ? t("tenants.provisioning.identity.cancelConfirmCreated")
            : t("tenants.provisioning.identity.cancelConfirmFound")}
        </p>
      </div>

      {error && (
        <p className="field-error" role="alert" data-testid="provisioning-cancel-error">
          {error}
        </p>
      )}

      <div className="modal__actions">
        <button
          type="button"
          className="btn"
          onClick={onClose}
          disabled={busy}
          data-testid="provisioning-cancel-dismiss"
        >
          {t("tenants.cancel")}
        </button>
        <button
          type="button"
          className="btn btn--danger"
          onClick={onConfirm}
          disabled={busy}
          data-testid="provisioning-cancel-submit"
        >
          {busy
            ? t("tenants.provisioning.identity.cancelling")
            : t("tenants.provisioning.identity.cancelConfirmSubmit")}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Step 3 — which applications the customer has bought. A replace and not an add/remove pair: the step is a
 * statement of the purchase, so the form always sends the whole answer, and saving the same answer twice
 * changes nothing (the backend writes and audits only on a real change).
 *
 * IT IS ALSO THE TENANT'S FRONT DOOR (task aa0c3eb7), and that is what the two extra things here are for.
 * `clms` is the code `ApplicationEntitlementGate` asks for on every `/api` and `/scorm` request, so:
 *
 *   * WHEN IT IS ALREADY MISSING the step SAYS the customer is shut out, above the grid, in the form that is
 *     true of this tenant — nothing recorded yet, or recorded without this application and when. An unticked
 *     box is not that statement: it reads as a purchase nobody recorded, which is exactly how a tenant
 *     halfway through provisioning and a customer whose applications were taken away came to look alike.
 *   * WHEN THE SELECTION WOULD REMOVE IT the submit stops being "Tallenna sovellusoikeudet": it carries the
 *     danger tone and says what it does, with the consequence spelled out beside it. The confirmation itself
 *     is the section's, because it owns the request.
 *
 * `hrm` does neither, and the step does not special-case it to achieve that: it is `closesAccess` reading
 * only the code the door asks for, so an application that gates nothing reads as the record it is.
 */
function EntitlementsStep({
  recorded,
  recordedAt,
  chosen,
  onToggle,
  busy,
  disabled,
  frozen,
  onSubmit,
}: {
  /** The server's stored set — what access the customer has NOW, as opposed to what the boxes propose. */
  recorded: readonly string[];
  /** When that answer was last recorded, or null when none ever was (task aa0c3eb7). */
  recordedAt: string | null;
  chosen: readonly string[];
  onToggle: (code: string, on: boolean) => void;
  busy: boolean;
  disabled: boolean;
  /** The tenant is frozen: the purchase still reads, as a list rather than as a form. */
  frozen: boolean;
  onSubmit: (e: React.FormEvent) => void;
}) {
  const { t } = useTranslation();
  const state = accessState(recorded, recordedAt);
  /** This save would take a served customer's access away — the one that is confirmed. */
  const willClose = closesAccess(recorded, chosen);

  /**
   * The customer is shut out as things stand, said in the form that is TRUE of this tenant (task aa0c3eb7).
   * The two differ in what somebody should do next, which is the whole reason the backend records a date for
   * step 3's answers:
   *
   *   * NEVER OPENED — nothing is on record. This onboarding has not reached step 3, so the remedy is to
   *     finish it, and the sentence says the customer is waiting rather than implying a decision.
   *   * CLOSED — an answer IS on record and it does not include this application, with the date it was
   *     recorded. Somebody decided this; the console does not guess at WHY, because no reason is stored.
   *
   * Stated for a frozen tenant too, and there it is the fact a THAW does not fix: `resumeTenant` opens the
   * doors the freeze closed and this one stays shut, so an operator who thawed a tenant and heard nothing
   * from the customer would otherwise have no way to know why. The frozen branch says so in its own words
   * rather than reusing the live sentence.
   */
  const closedNotice = state !== "served" && (
    <p
      className="banner banner--error"
      role="status"
      data-testid="provisioning-entitlements-access-closed"
    >
      {state === "closed"
        ? t("tenants.provisioning.entitlements.accessClosed", {
            date: formatDate(new Date(recordedAt!)),
          })
        : t("tenants.provisioning.entitlements.accessNeverOpened")}{" "}
      {t(
        frozen
          ? "tenants.provisioning.entitlements.accessClosedFrozen"
          : "tenants.provisioning.entitlements.accessClosedRemedy",
      )}
    </p>
  );

  // Frozen: the operator still has to be able to SEE what the customer bought — it is the one fact this
  // step holds — so it is listed as text. Checkboxes nobody can save would be a form pretending to work.
  if (frozen) {
    return (
      <div className="provisioning__form">
        {closedNotice}
        <ul className="provisioning__applied" data-testid="provisioning-entitlements-frozen">
          {chosen.length === 0 ? (
            <li>{t("tenants.provisioning.entitlements.none")}</li>
          ) : (
            chosen.map((code) => <li key={code}>{t(entitlementLabelKey(code))}</li>)
          )}
        </ul>
        <p className="provisioning__hint" role="note">
          {t("tenants.provisioning.frozenReadOnly")}
        </p>
      </div>
    );
  }

  return (
    <form className="form provisioning__form" onSubmit={onSubmit}>
      {closedNotice}

      <fieldset className="provisioning__entitlements">
        <legend>{t("tenants.provisioning.entitlements.legend")}</legend>
        {APPLICATION_ENTITLEMENTS.map((code) => (
          <label key={code} className="provisioning__check">
            <input
              type="checkbox"
              checked={chosen.includes(code)}
              onChange={(e) => onToggle(code, e.target.checked)}
              data-testid={`provisioning-entitlement-${code}`}
            />
            {t(entitlementLabelKey(code))}
          </label>
        ))}
      </fieldset>

      {/* The consequence BEFORE the button and not inside the dialog alone: an operator who has just
          unticked the box should not have to press a primary-looking "save" to find out what it does. */}
      {willClose && (
        <p className="field-error" role="alert" data-testid="provisioning-entitlements-close-warning">
          {t("tenants.provisioning.entitlements.closeWarning")}
        </p>
      )}

      <div className="provisioning__step-actions">
        <button
          type="submit"
          className={`btn ${willClose ? "btn--danger" : "btn--primary"}`}
          disabled={disabled}
          data-testid="provisioning-entitlements-submit"
        >
          {busy
            ? t(
                willClose
                  ? "tenants.provisioning.entitlements.closing"
                  : "tenants.provisioning.entitlements.saving",
              )
            : willClose
              ? t("tenants.provisioning.entitlements.closeSubmit")
              : t("tenants.provisioning.entitlements.save")}
        </button>
      </div>
    </form>
  );
}

/**
 * The confirmation for the one step-3 save that shuts a customer out (task aa0c3eb7) — an `alertdialog`,
 * like the freeze's and the invite cancel's.
 *
 * IT ASKS FOR THE SLUG, which is the freeze's guard (`TenantFreezeSection`) and is here for the same two
 * reasons: the consequence is the freeze's consequence — every door of one customer, its pääkäyttäjä exactly
 * as its learners — and the tenant page is reached from a directory where four customers can read alike. The
 * alternative was the weaker guard on the cheaper-looking path, which would have made the checkbox grid the
 * way to shut a customer without the freeze's ceremony.
 *
 * WHAT IT DOES NOT ASK FOR IS A REASON, and that is the API's doing rather than a judgement that the reason
 * matters less: `SetTenantEntitlementsRequest` carries entitlements and nothing else, so a field here would
 * collect a sentence the request drops. What the console can do instead is say which of the two acts the
 * operator wants — so the body names the FREEZE, which does record a reason and is reversible as one act, for
 * the case where the point is to shut a customer temporarily. Removing the entitlement is for the other case:
 * the contract is over, and that is the fact only this step records.
 */
function CloseAccessDialog({
  slug,
  typed,
  onTyped,
  error,
  busy,
  onConfirm,
  onClose,
}: {
  /** The tenant's slug — the string that has to be typed, and the only name this console has for it. */
  slug: string;
  typed: string;
  onTyped: (value: string) => void;
  error: string | null;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const titleId = "provisioning-close-title";
  const bodyId = "provisioning-close-body";
  const confirmRef = useRef<HTMLInputElement>(null);
  // Matched EXACTLY, as the freeze matches it: the slug is lowercase by construction, so this costs a
  // careful operator nothing and is the only comparison that makes the control do what it says.
  const confirmed = typed === slug;

  return (
    <Modal
      role="alertdialog"
      className="provisioning__close-dialog"
      labelledBy={titleId}
      describedBy={bodyId}
      testId="provisioning-close-dialog"
      onClose={onClose}
      closeDisabled={busy}
      initialFocus={confirmRef}
    >
      <h2 id={titleId}>{t("tenants.provisioning.entitlements.closeConfirmTitle", { tenant: slug })}</h2>
      <div id={bodyId}>
        {/* A list and not a paragraph, for the reason the freeze's effects are one: an operator reading a
            confirmation reads the bullets, and the one that must be seen before they answer is that this is
            not the freeze and nothing here records why. */}
        <ul className="freeze__effects">
          {CLOSE_ACCESS_EFFECTS.map((key) => (
            <li key={key}>{t(key)}</li>
          ))}
        </ul>
        <p data-testid="provisioning-close-not-freeze">
          {t("tenants.provisioning.entitlements.closeConfirmNotFreeze")}
        </p>
      </div>

      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (confirmed && !busy) onConfirm();
        }}
        noValidate
      >
        <label htmlFor="provisioning-close-confirm">
          {t("tenants.provisioning.entitlements.closeConfirmLabel", { tenant: slug })}
        </label>
        <input
          id="provisioning-close-confirm"
          ref={confirmRef}
          type="text"
          value={typed}
          onChange={(e) => onTyped(e.target.value)}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          disabled={busy}
          data-testid="provisioning-close-confirm"
        />

        {/* The refusal is reported INSIDE the dialog: the step's own error line sits on the page behind it,
            where an operator who had just answered a confirmation would not see it. */}
        {error && (
          <p className="field-error" role="alert" data-testid="provisioning-close-error">
            {error}
          </p>
        )}

        <div className="modal__actions">
          <button
            type="button"
            className="btn"
            onClick={onClose}
            disabled={busy}
            data-testid="provisioning-close-dismiss"
          >
            {t("tenants.cancel")}
          </button>
          <button
            type="submit"
            className="btn btn--danger"
            disabled={!confirmed || busy}
            data-testid="provisioning-close-submit"
          >
            {busy
              ? t("tenants.provisioning.entitlements.closing")
              : t("tenants.provisioning.entitlements.closeConfirmSubmit")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Step 4 — the default content the customer starts with (ADR-0014 §8, §12 D-2).
 *
 * THE FORM IS OFFERED WHETHER OR NOT THE STEP IS DONE, like step 2's, but for a different reason: step 2 is
 * retryable because a re-invite re-sends a mail, while this one is retryable because the customer may be
 * given MORE content later. What it is not is a way to take content back — an applied item is the tenant's
 * own course and only the tenant can delete it (§8) — so there is no unticking of what is already there,
 * and what is already there is shown as a list rather than as checked boxes somebody might try to clear.
 *
 * A WITHDRAWN VERSION STILL APPEARS. A copied version that is no longer in the catalogue (the class-B
 * missing target, ADR-0011 §3.4) is listed as withdrawn and not dropped: the tenant has the course either
 * way, and silently omitting it would make the step's list disagree with the customer's catalogue.
 *
 * WITHDRAWN AND SUPERSEDED ARE NOT THE SAME FACT, so the two lists below come from two different views of
 * the same response. What is OFFERED is the newest version per item (`newestPerItem`) — nobody should be
 * given a version Verso has replaced. What the tenant ALREADY HAS is resolved against the UNNARROWED
 * catalogue: versions accumulate under §8 and the route returns all of them, so the moment Verso publishes
 * v2 of an item a customer holds v1 of, narrowing first would leave v1 unresolvable and the step would call
 * a version that is sitting in the library withdrawn. Withdrawn means absent from the whole catalogue.
 */
function DefaultContentStep({
  library,
  applied,
  picks,
  onToggle,
  busy,
  disabled,
  frozen,
  onSubmit,
}: {
  /** The catalogue, every published version — or null while it loads, or after its read failed. */
  library: ContentLibraryItem[] | null;
  /** The library version ids this tenant has already been given. */
  applied: readonly string[];
  picks: readonly string[];
  onToggle: (libraryItemId: string, on: boolean) => void;
  busy: boolean;
  disabled: boolean;
  /** The tenant is frozen: what it already holds still lists, nothing more can be copied in. */
  frozen: boolean;
  onSubmit: (e: React.FormEvent) => void;
}) {
  const { t } = useTranslation();

  if (library === null) {
    return (
      <p className="empty-state" role="status" data-testid="provisioning-library-loading">
        {t("tenants.provisioning.defaultContent.libraryLoading")}
      </p>
    );
  }

  const appliedIds = new Set(applied);
  // Resolved against the WHOLE catalogue, so a superseded version still has a title and a number to show.
  const appliedItems = library
    .filter((item) => appliedIds.has(item.id))
    // Two rows share a title exactly when they are two versions of one item, so the tie-break is the
    // version — compared and not subtracted, because the contract types an int32 `number | string`.
    .sort(
      (a, b) =>
        a.title.localeCompare(b.title) ||
        (a.version < b.version ? -1 : a.version > b.version ? 1 : 0),
    );
  const resolved = new Set(appliedItems.map((item) => item.id));
  const withdrawn = applied.filter((id) => !resolved.has(id));

  /* What the tenant already has. Text and not controls: nothing on this console can take it back. */
  const appliedList = applied.length > 0 && (
    <ul className="provisioning__applied" data-testid="provisioning-content-applied">
      {appliedItems.map((item) => (
        <li key={item.id} data-testid={`provisioning-content-applied-${item.id}`}>
          {t("tenants.provisioning.defaultContent.appliedItem", {
            title: item.title,
            version: item.version,
          })}
        </li>
      ))}
      {withdrawn.map((id) => (
        <li key={id} data-testid={`provisioning-content-withdrawn-${id}`}>
          {t("tenants.provisioning.defaultContent.withdrawnItem")}
        </li>
      ))}
    </ul>
  );

  // Verso has published nothing, so there is nothing to offer and no retry that could change it — which is
  // the same fact the backend reports as the step's `not_available` state. Said in words here, because the
  // badge alone does not tell an operator that the gap is Verso's rather than theirs.
  //
  // The applied list comes WITH that sentence, not instead of it. An empty catalogue is precisely the state
  // where every copy the tenant holds is unresolvable, so returning the sentence alone would drop the whole
  // record of what the customer has — the one outcome the withdrawn row above exists to prevent.
  if (library.length === 0) {
    return (
      <div className="provisioning__form">
        {appliedList}
        <p className="provisioning__hint" role="note" data-testid="provisioning-library-empty">
          {t("tenants.provisioning.defaultContent.libraryEmpty")}
        </p>
      </div>
    );
  }

  // Offered from the newest version per item: the operator picks what to GIVE, and a version Verso has
  // already replaced is not something to hand a new customer.
  const offered = newestPerItem(library).filter((item) => !appliedIds.has(item.id));

  // Frozen: what the customer already holds still reads — those are its own courses — but nothing more is
  // copied into a tenant whose doors are shut.
  if (frozen) {
    return (
      <div className="provisioning__form">
        {appliedList}
        <p className="provisioning__hint" role="note">
          {t("tenants.provisioning.frozenReadOnly")}
        </p>
      </div>
    );
  }

  return (
    <form className="form provisioning__form" onSubmit={onSubmit} data-testid="provisioning-content-form">
      {appliedList}

      {offered.length === 0 ? (
        <p className="provisioning__hint" role="note" data-testid="provisioning-content-all-applied">
          {t("tenants.provisioning.defaultContent.allApplied")}
        </p>
      ) : (
        <>
          <fieldset className="provisioning__entitlements">
            <legend>{t("tenants.provisioning.defaultContent.legend")}</legend>
            {offered.map((item) => (
              <label key={item.id} className="provisioning__check">
                <input
                  type="checkbox"
                  checked={picks.includes(item.id)}
                  onChange={(e) => onToggle(item.id, e.target.checked)}
                  data-testid={`provisioning-content-${item.id}`}
                />
                {t("tenants.provisioning.defaultContent.offeredItem", {
                  title: item.title,
                  version: item.version,
                })}
              </label>
            ))}
          </fieldset>

          <div className="provisioning__step-actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={disabled}
              data-testid="provisioning-content-submit"
            >
              {busy
                ? t("tenants.provisioning.defaultContent.applying")
                : t("tenants.provisioning.defaultContent.apply")}
            </button>
          </div>
        </>
      )}
    </form>
  );
}
